import { getSaeSetting } from '@/apis/equipment'
import sampleCabinet1 from '@/assets/sample/sample_cabinet1.png'
import sampleCabinet2 from '@/assets/sample/sample_cabinet2.png'
import sampleCabinet3 from '@/assets/sample/sample_cabinet3.png'
import sampleCabinet4 from '@/assets/sample/sample_cabinet4.png'
import { sampleCdn } from '@/constants'
import { OSS_NC_BASE_URL } from '@/data/constant'
import { translateLang } from '@/data/i18n/translateLang'
import { IBigPlank } from '@/partTypes'
import store from '@/store'
import { toDecimal } from '@/util/commonFuncs'
import { fileNameSetObj } from '@/util/configure'
import {
  Exporter,
  FolderStructureBuilder,
  collector,
} from '@/util/file/customFolderGenerate'
import {
  FileNode,
  FileSource,
  FolderNode,
} from '@/util/file/customFolderGenerate/types'
import {
  genBigPlankPath,
  getEdgeLengthsFromLShape,
  getPathMinMaxPoint,
  isLShapeSurplusBigPlank,
} from '@/util/plankCommonFuncs'
import {
  _translateFileNameFn,
  appendIpToJsonBlob,
  dealJinxiPaibanFolder,
  dealTransFileName,
  genContentPromises,
  genEngravingFileName,
} from '@/util/saveFile'
import { jsPdfTool } from '@/util/tag/pdfFont'
import { xyPointReverse } from '@/util/transform/graph'
import { Folder } from '@/views/fileManage/components/m-custom-folder/types'
import { FolderTreeManager } from '@/views/fileManage/components/m-custom-folder/utils/folderControl'
import { createFolder } from '@/views/fileManage/components/m-custom-folder/utils/folderFactory'
import { getSvgListToPdfJSBinary } from '@/views/printTag/utils/createPdf'
import { uploadFileToOss } from '@yige/utils'
import FileSaver from 'file-saver'
import Vue from 'vue'

/**
 *
 * @param arr 文件信息数组
 * @param name 打包名称
 * @param imgArr 排版图数组
 * @param isNeedRecordSize 是否需要记录
 * @param paibanWayName 排版历史名称
 * @param mergeFolderFun 文件夹合并方法
 * @param ncSetting nc设置
 * @param callBack 回调函数
 * @returns 无
 */

export async function downloadFile(
  /** 全部文件数组 */
  arr: any[],
  name: string,
  imgArr: any[],
  pdfArr: any[],
  isNeedRecordSize: boolean,
  paibanWayName?: string,
  mergeFolderFun?: any,
  ncSetting?: any,
  callBack?: () => void,
  jsonBlob?: any,
  workerShopBlob?: any,
  operateFun?: () => void,
  /** skipNativeDownload: 为 true 时跳过内嵌环境下的原生/Qt 下载，仅走 web 端打包流程（如发送车间场景仅生成不触发本地下载） */
  options: {
    skipNativeDownload?: boolean
  } = {
    skipNativeDownload: false,
  }
) {
  const len = arr.length
  let err_tip = true //防止多次弹出提示
  const error_message: any[] = []
  const noCutErrorInfo = []
  for (let i = 0; i < len; ++i) {
    const cut_it = arr[i]
    if (cut_it.error_info && err_tip && !cut_it.plank_size_abnormal) {
      err_tip = false
      return {
        isErr: true,
        errMsg: cut_it.error_info,
      }
    }
    if (cut_it.filetype == 'nc' && cut_it.plank_size_abnormal) {
      error_message.push(cut_it.error_info)
      return {
        isErr: true,
        errMsg: cut_it.error_info,
      }
    }
    if (err_tip && cut_it.no_cut_error_info) {
      err_tip = false
      noCutErrorInfo.push(cut_it.no_cut_error_info)
    }
  }
  if (error_message.length) {
    Vue.prototype.$modalMsg(error_message, 'error', '警告')
  }

  mergeFolderFun(ncSetting)
  const win = window as any
  // 内嵌环境且未要求跳过原生下载时，走 Qt 下载
  if (win.webToQt && !options.skipNativeDownload) {
    const res = await dealWebToQtData(arr, imgArr, pdfArr, ncSetting)
    if (res && operateFun) {
      operateFun()
      return res
    }
    if (callBack) callBack()
    return
  }

  const { cache, zip, promises, ncSuffix }: any = await genContentPromises(
    arr,
    ncSetting?.customizeFolder,
    isNeedRecordSize,
    jsonBlob,
    workerShopBlob,
    name,
    // 需要兼容删除生产线问题 不适用store.state.ncSetting?.process_setting_id
    ncSetting?.process_setting_id
  )
  // 判断合并文件夹 排班页下载NC -- 排版图
  const folderSetting =
    store.state.mergeFolderSetting ?? ncSetting.customizeFolder
  for (const key in imgArr) {
    imgArr[key].forEach((item: any) => {
      let paibanFileName = `排版图${item.index}`
      if (store.state.preferencesSetting?.setting?.isCustomPaibanImg) {
        paibanFileName = key
      }
      let translateFileName = _translateFileNameFn(
        paibanFileName,
        ncSetting.customizeFolder,
        paibanFileName,
        item,
        true
      )
      if (checkIsMergeFolder(`${paibanFileName}文件`)) {
        const tempFileNameStr = dealTransFileName(item).slice(0, -1)
        translateFileName += `/${tempFileNameStr}`
      }
      let fileName = translateFileName + '/' + item.name
      // 开启 将雕刻机正/反面文件用不同文件夹放置 功能后 将排版图放入下料文件F中
      if (folderSetting.engraving_front_back_folder) {
        // 获取下料文件名
        const engravingTranslateFileName = genEngravingFileName(
          item,
          folderSetting
        )
        let nextFileName = ''
        const stockKey = item.stock_key.substring(
          0,
          item.stock_key.lastIndexOf(':')
        )
        if (
          ncSetting.folderCategory &&
          ncSetting.folderCategory.useCategoryFolder
        ) {
          // 截取stock_key，获取颜色、材质、厚度相同的文件放入相同文件夹
          nextFileName = stockKey.replace(/:/g, '_') + '/'
        }
        // 按照材质颜色厚度尺寸得到其文件夹名称，优先级比颜色材质厚度高
        if (ncSetting.folderCategory?.use_category_size_folder) {
          nextFileName =
            (getFolderClassifyNameByMatCodeTextureThickSize(item.stock_key) ??
              nextFileName) + '/'
        }
        fileName = engravingTranslateFileName + 'F/' + nextFileName + item.name
      }
      const url = item.url.replace(/^data:image\/(png|jpg|jpeg);base64,/, '')
      const nameList = fileName.split('/')
      if (nameList.length) {
        if (
          nameList[0] === ncSetting.customizeFolder.merge_folder_display_name &&
          nameList[1] &&
          store.state.ncSetting.customizeFolder?.merge_folder_list.length > 1
        ) {
          if (
            store.state.tempFileName.includes(nameList[nameList.length - 1])
          ) {
            const tempList = JSON.parse(
              JSON.stringify(store.state.repeatFileName)
            )
            tempList.push(nameList[nameList.length - 1])
            store.commit('setRepeatFileName', tempList)
          } else {
            const tempList = JSON.parse(
              JSON.stringify(store.state.tempFileName)
            )
            tempList.push(nameList[nameList.length - 1])
            store.commit('setTempFileName', tempList)
          }
        }
      }
      const newFileName = dealJinxiPaibanFolder(
        fileName,
        ncSetting.customizeFolder
      )
      zip.file(newFileName, url, { base64: true })
      cache[newFileName] = url
    })
  }
  // 排版图 顺带导出pdf功能
  for (const key in pdfArr) {
    const item = pdfArr[key]
    if (item?.svg.length) {
      let translateFileName = _translateFileNameFn(
        item.name,
        ncSetting.customizeFolder,
        item.name,
        item,
        true
      )
      if (translateFileName == item.name) {
        translateFileName = ''
      }
      const binary = await convertPaibanPdfToBinary(item)
      const fileName = translateFileName
        ? translateFileName + '/' + `${item.name}.pdf`
        : `${item.name}.pdf`
      zip.file(fileName, new Blob([binary]), {
        binary: true,
      })
    }
  }
  return {
    zip,
    nameStr: name,
    promises,
    ncSuffix,
    noCutErrorInfo,
  }
}

/**
 * @description 转换排版图 PDF 数据为二进制
 * @param paibanPdfData
 */
async function convertPaibanPdfToBinary(paibanPdfData: {
  templateData: any
  svg: string[]
}) {
  await jsPdfTool.loadFontByTagTemp([
    ...[{ data: paibanPdfData.templateData.bigPlank, type: 'bigPlank' }],
    ...[{ data: paibanPdfData.templateData.part, type: 'part' }],
  ])
  // svg直转 pdf
  return await getSvgListToPdfJSBinary(paibanPdfData.svg, {
    unit: 'mm',
    format: [
      paibanPdfData.templateData.width,
      paibanPdfData.templateData.height,
    ],
  })
}

export function mergeFolder(ncSetting: any) {
  const folderSetting = ncSetting
    ? ncSetting
    : store.state.ncSetting.customizeFolder
  /** 意义不明的代码 */
  // const mergeFolderList = folderSetting.merge_folder_list
  // const mergeFolderDisplayName = folderSetting.merge_folder_display_name
  // 如果有合并文件
  let needMerge = folderSetting?.merge_folder_list?.length > 1
  if (folderSetting?.use_category_folder) {
    // 如果为true，那么就不使用合并文件
    needMerge = false
  }
  return needMerge
}

// 处理web和客户端通信时的数据 内嵌
// TODO标记
export async function dealWebToQtData(
  arr: any[],
  imgArr: any[],
  pdfArr: any[],
  ncSetting: any
) {
  try {
    const qtData = []
    // 处理排版图
    for (const key in imgArr) {
      imgArr[key]?.forEach((item: any) => {
        const url = item.url.replace(/^data:image\/(png|jpg|jpeg);base64,/, '')

        let paibanFileName = `排版图${item.index}`
        if (store.state.preferencesSetting?.setting?.isCustomPaibanImg) {
          paibanFileName = key
        }
        let translateFileName = _translateFileNameFn(
          paibanFileName,
          ncSetting.customizeFolder,
          paibanFileName,
          item,
          true
        )
        if (checkIsMergeFolder(`${paibanFileName}文件`)) {
          const tempFileNameStr = dealTransFileName(item).slice(0, -1)
          translateFileName += `/${tempFileNameStr}`
        }
        const obj = {
          originFile: translateFileName,
          fileName: item.name,
          url: url,
          fileType: 'base64',
        }
        qtData.push(obj)
      })
    }
    // 排版图 导出pdf
    for (const key in pdfArr) {
      const item = pdfArr[key]
      if (item?.svg.length) {
        let translateFileName = _translateFileNameFn(
          item.name,
          ncSetting.customizeFolder,
          item.name,
          item,
          true
        )
        if (translateFileName == item.name) {
          translateFileName = ''
        }
        const binary = await convertPaibanPdfToBinary(item)
        const uploadFile = new File([binary], `${key}.pdf`, {
          type: '',
          lastModified: Date.now(),
        })
        // 上传文件
        const ossUrl = await uploadFileToOss(
          uploadFile,
          () => null,
          () => null,
          'productionWorkShop',
          true
        )
        const obj = {
          originFile: translateFileName,
          fileName: `${item.name}.pdf`,
          url: ossUrl,
          fileType: 'url',
        }
        qtData.push(obj)
      }
    }

    let repeatZipFileNameList = [] as any
    const tempZipFileNameList = [] as any
    const ignoreList = [
      'PTP_folder',
      'dock5F_drill_folder',
      'dock6F_drill_folder',
      'door_wall_aio_folder',
      'side_hole_folder',
      'electronic_saw_folder',
    ].map((item) =>
      ncSetting.customizeFolder ? ncSetting.customizeFolder[item] : ''
    )
    let sawSetting
    const saw_setting_res = await getSaeSetting({
      setting_id: store.state.settingId || ncSetting.process_setting_id,
      setting_type: 'electronicSaw',
    })
    if (saw_setting_res.data.data) sawSetting = saw_setting_res.data.data
    const { orderType, orderCounts, OrderTypeMap } = dealElectronicSawData(arr)
    // 处理下料文件
    let num = 1
    const { is_gen_surplus_folder } = ncSetting.customizeFolder
    for (let i = 0; i < arr.length; ++i) {
      const item = arr[i]
      const url =
        arr[i].fileType == 'tag'
          ? arr[i].url
          : 'https://eggrj.oss-cn-hangzhou.aliyuncs.com/' + arr[i].filename
      // 如果是贴标机文件
      let prefixName = ''
      let originFile = ''
      let fileName = ''
      let nextFileName = ''
      const { useCategoryFolder, label_image_in_nc_folder } =
        ncSetting.folderCategory

      // 需要判断当前用户是否拥有贴标图片跟随贴标文件放在NC文件夹的权限
      const hasLabelImageInNcFolderPermission =
        store.state.userPermissions['label_image_in_nc_folder'] &&
        label_image_in_nc_folder
      const surplusFolder = _translateFileNameFn(
        '余料大板文件',
        ncSetting.customizeFolder,
        'surplus_folder',
        arr[i]
      )
      if (
        ncSetting.folderCategory &&
        useCategoryFolder &&
        (arr[i].filetype == 'label' || arr[i].filetype == 'nc')
      ) {
        prefixName =
          arr[i].stock_key
            .substring(0, arr[i].stock_key.lastIndexOf(':'))
            .replace(/:/g, '_') + '/'
      }
      const obj = {} as { [key: string]: any }
      if (arr[i].filetype === 'label') {
        const labeling_folder = _translateFileNameFn(
          '贴标机',
          ncSetting.customizeFolder,
          'labeling_folder',
          arr[i]
        )
        originFile = prefixName + labeling_folder
        if (
          hasLabelImageInNcFolderPermission &&
          store.state.isFollowExportTag
        ) {
          originFile =
            prefixName +
            _translateFileNameFn(
              '下料文件',
              ncSetting.customizeFolder,
              'engraving_folder',
              arr[i]
            ) +
            '/label/'
        } else if (is_gen_surplus_folder && item.plank_type === 'surplus') {
          originFile = prefixName + `${surplusFolder}/${labeling_folder}`
        }
        if (item.stock_key && checkIsMergeFolder('贴标机文件')) {
          const templateString = dealTransFileName(item)
          originFile = originFile.endsWith('/')
            ? `${originFile}${templateString}`
            : `${originFile}/${templateString}`
        }

        fileName = arr[i].file_display_name
      } else if (arr[i].filetype === 'nc') {
        const engraving_folder = _translateFileNameFn(
          `下料文件`,
          ncSetting.customizeFolder,
          'engraving_folder',
          arr[i]
        )
        originFile = prefixName + engraving_folder
        if (is_gen_surplus_folder && item.plank_type === 'surplus') {
          originFile = prefixName + `${surplusFolder}/${engraving_folder}`
        }
        if (item.stock_key && checkIsMergeFolder('雕刻机文件')) {
          const templateString = dealTransFileName(item)
          originFile = originFile.endsWith('/')
            ? `${originFile}${templateString}`
            : `${originFile}/${templateString}`
        }
        // 不同工位分文件夹
        let workstationFileName = ''
        if (
          ncSetting.customizeFolder?.class_by_workstations &&
          ncSetting?.single_double_station === 'double' &&
          !ncSetting.fileMerge?.doubleStationCombine &&
          !ncSetting.fileMerge?.singleStationCombine
        ) {
          if (arr[i].workstation === 'odd') {
            workstationFileName = '工位一/'
          } else if (arr[i].workstation === 'even') {
            workstationFileName = '工位二/'
          }
          originFile = originFile.endsWith('/')
            ? `${originFile}${workstationFileName}`
            : `${originFile}/${workstationFileName}`
        }
        if (arr[i].file_display_name == '') {
          fileName =
            ('00' + num).substr(-3, 3) +
            '_' +
            (arr[i].direction == 'front' ? '正.' : '反.') +
            arr[i].filetype
          num++
        } else {
          fileName = arr[i].file_display_name
        }
      } else if (arr[i].file_type == '玻璃切割机') {
        let fName = '玻璃切割机'
        const { use_category_folder } = ncSetting.glass_setting ?? {}
        if (use_category_folder) {
          fName = arr[i].stock_key.replace(/:/g, '_') + '/'
        }
        originFile = prefixName + fName
        fileName = arr[i].file_display_name
      } else if (arr[i].fileType == 'tag') {
        if (arr[i].exportType === 'pdf') {
          // pdf
          FileSaver.saveAs(arr[i].fileData, '标签图.pdf')
        } else {
          if (arr[i].tagData && arr[i].tagData?.length) {
            arr[i].tagData.forEach((item: any) => {
              const url = item.url.replace(
                /^data:image\/(png|jpg|jpeg);base64,/,
                ''
              )
              let oriFile = _translateFileNameFn(
                '标签图',
                ncSetting.customizeFolder,
                'label_image_folder',
                arr[i]
              )
              if (
                hasLabelImageInNcFolderPermission &&
                store.state.isFollowExportTag
              ) {
                oriFile = _translateFileNameFn(
                  '下料文件',
                  ncSetting.customizeFolder,
                  'engraving_folder',
                  arr[i]
                )
                oriFile = oriFile ? oriFile + '/label/image/' : ''
              }
              if (item.stock_key && checkIsMergeFolder('贴标图片文件')) {
                const templateString = dealTransFileName(item)
                oriFile = oriFile.endsWith('/')
                  ? `${oriFile}${templateString}`
                  : `${oriFile}/${templateString}`
              }
              const obj = {
                originFile: oriFile,
                fileName: `${item.fileName}.${
                  ncSetting.labelImageFormat || 'jpg'
                }`,
                url: url,
                fileType: 'base64',
              }
              qtData.push(obj)
            })
          } else {
            originFile = _translateFileNameFn(
              '标签图',
              ncSetting.customizeFolder,
              'label_image_folder',
              arr[i]
            )
            if (
              hasLabelImageInNcFolderPermission &&
              store.state.isFollowExportTag
            ) {
              originFile = _translateFileNameFn(
                '下料文件',
                ncSetting.customizeFolder,
                'engraving_folder',
                arr[i]
              )
              originFile = originFile ? originFile + '/label/image/' : ''
            }
            const afterType = arr[i].url.endsWith('.pdf') ? '.pdf' : '.zip'
            if (arr[i].file_display_name) {
              fileName = arr[i].file_display_name + afterType
            } else {
              fileName = arr[i].filename + afterType
            }
          }
        }
      } else if (arr[i].file_type === 'saw') {
        originFile = _translateFileNameFn(
          'saw',
          ncSetting.customizeFolder,
          'electronic_saw_folder',
          arr[i]
        )
        originFile = originFile ? `${originFile}/` : ''

        if (sawSetting.rooms_by_category) {
          const delimiter = sawSetting?.delimiter || ''
          let lv2Folder = ''
          let lv3Folder = ''
          if (sawSetting.useCategoryFolder) {
            switch (orderType) {
              case OrderTypeMap['noOrder']:
              case OrderTypeMap['singleOrder']:
                if (arr[i].room_name) {
                  const newRemark = arr[i].room_remark
                    ? arr[i].room_remark.replace(/[/:?<>\|."*]/g, '')
                    : ''
                  nextFileName = `${arr[i].room_name}${
                    newRemark ? delimiter + newRemark : ''
                  }`
                } else if (arr[i].partHasRoom) {
                  nextFileName = '其他房间'
                }
                break
              case OrderTypeMap['multiOrder']:
                if (arr[i].order_no) {
                  const newCustomer = arr[i].customer_name
                    ? arr[i].customer_name.replace(/[/:?<>\|."*]/g, '')
                    : ''
                  lv2Folder = `${arr[i].order_no}${
                    newCustomer ? delimiter + newCustomer : ''
                  }`
                } else {
                  lv2Folder = '其他订单'
                }
                if (arr[i].room_name) {
                  const newRemark = arr[i].room_remark
                    ? arr[i].room_remark.replace(/[/:?<>\|."*]/g, '')
                    : ''
                  lv3Folder = `${arr[i].room_name}${
                    newRemark ? delimiter + newRemark : ''
                  }`
                } else if (
                  orderCounts[arr[i].order_no]?.length !== 1 &&
                  orderCounts[arr[i].order_no].some(
                    (item: any) => item.room_name
                  )
                ) {
                  lv3Folder = arr[i].partHasRoom ? '其他房间' : ''
                }
                nextFileName = `${lv2Folder}${lv3Folder ? '/' + lv3Folder : ''}`
                break
              default:
                break
            }
          } else {
            switch (orderType) {
              case OrderTypeMap['noOrder']:
                break
              case OrderTypeMap['singleOrder']:
                break
              case OrderTypeMap['multiOrder']:
                if (arr[i]?.partHasOrder) {
                  nextFileName = '其他订单'
                } else {
                  const newCustomer = arr[i].customer_name
                    ? arr[i].customer_name.replace(/[/:?<>\|."*]/g, '')
                    : ''
                  nextFileName = `${arr[i].order_no}${
                    newCustomer ? delimiter + newCustomer : ''
                  }`
                }
                break
              default:
                break
            }
          }

          originFile += nextFileName ? `${nextFileName}/` : ''
        }

        if (arr[i].file_display_name) {
          fileName = arr[i].file_display_name
        } else {
          fileName = arr[i].fileName.split('/')[1]
        }
      } else {
        originFile =
          arr[i]?.file_type === 'robotic_arm'
            ? ''
            : _translateFileNameFn(
                arr[i].filetype ?? arr[i].fileType,
                ncSetting.customizeFolder,
                arr[i].filetype,
                arr[i]
              )
        if (is_gen_surplus_folder && item.plank_type === 'surplus') {
          originFile = `${surplusFolder}/${originFile}/`
        }
        if (arr[i].file_display_name) {
          fileName = arr[i].file_display_name
        } else {
          fileName = arr[i].filename?.split('/')[1]
        }
      }
      const newFileName = dealJinxiPaibanFolder(fileName, surplusFolder)
      const newOriginFile = dealJinxiPaibanFolder(originFile, surplusFolder)
      obj.url = url
      obj.fileName = newFileName
      obj.originFile = newOriginFile
      obj.fileType = 'url'
      qtData.push(obj)
      if (
        obj.originFile.indexOf(
          ncSetting.customizeFolder?.merge_folder_display_name
        ) !== -1 &&
        !ignoreList.includes(
          arr[i].filetype ?? ncSetting.customizeFolder['electronic_saw_folder']
        )
      ) {
        if (tempZipFileNameList.includes(obj.fileName)) {
          repeatZipFileNameList.push(obj.fileName)
        } else {
          // 存入值
          tempZipFileNameList.push(obj.fileName)
        }
      }
    }
    let repeatNameList = [] as any
    const repeatZipFolderNameList = [] as any
    // if (qtData) {
    //   // 遍历数据
    //   qtData.forEach((item) => {
    //     if (
    //       item.originFile.indexOf(
    //         ncSetting.customizeFolder?.merge_folder_display_name
    //       ) !== -1
    //     ) {
    //       if (tempZipFileNameList.includes(item.fileName)) {
    //         repeatZipFileNameList.push(item.fileName)
    //       } else {
    //         // 存入值
    //         tempZipFileNameList.push(item.fileName)
    //       }
    //     }
    //   })
    // }
    Object.keys(store.state?.folderContents)?.forEach((item) => {
      if (
        !ncSetting?.customizeFolder?.merge_folder_list.includes(
          fileNameSetObj[item] + '文件'
        ) &&
        ncSetting?.customizeFolder?.merge_folder_list.length > 1
      ) {
        if (
          ncSetting.customizeFolder.merge_folder_display_name.trim() ===
          store.state.folderContents[item].trim()
        ) {
          repeatZipFolderNameList.push(fileNameSetObj[item])
        }
      }
    })
    // 检查数据 TODO
    repeatZipFileNameList = [...new Set(repeatZipFileNameList)]
    repeatNameList = []
      .concat(repeatZipFolderNameList)
      .concat(repeatZipFileNameList)
    if (repeatNameList.length) {
      return {
        repeatNameList,
        repeatZipFolderNameList,
        repeatZipFileNameList,
        qtRepeat: true,
        zip: null,
        nameStr: null,
      }
    }
    if (
      (store.state.orderInfo && store.state.isTrialProduct) ||
      store.state.produceFileUrl
    ) {
      qtData.push(...sampleCdn)
    }
    dataToQt(qtData.filter((obj) => obj.url))
    return
  } catch (err) {
    Vue.prototype.$message({
      message: err,
      type: 'error',
    })
    throw err
  }
}
// 电子锯数据处理
export const dealElectronicSawData = (arr: any[]) => {
  const OrderTypeMap = {
    singleOrder: '单订单',
    multiOrder: '多订单',
    noOrder: '无订单',
  }
  let orderType = OrderTypeMap['singleOrder']
  const hasOrderNo = arr.some((item) => item.order_no)
  const hasNoOrderNo = arr.some((item) => !item.order_no)
  const hasRoom = arr.some((item) => item.room_name)
  const hasNoRoom = arr.some((item) => item.room_name)
  const getOrderNos = (data: any[]) => {
    return data
      .filter((item) => {
        if (item.file_type === 'saw') {
          return item
        }
      })
      .filter((item) => item.order_no)
      .map((item) => item.order_no)
  }
  // 获取所有order_no
  const allOrderNos = getOrderNos(arr)
  const uniqueOrderNos = new Set(allOrderNos) // 检查是否有数据缺少order_no
  // 根据订单数量设置orderType
  const hasMissingOrderNo = arr
    .filter((item) => {
      if (item.file_type === 'saw') return item
    })
    .some((item) => !item.order_no)
  if (!hasOrderNo) {
    orderType = OrderTypeMap['noOrder']
  } else if (hasMissingOrderNo || uniqueOrderNos.size > 1) {
    orderType = OrderTypeMap['multiOrder']
  } else if (uniqueOrderNos.size === 1) {
    orderType = OrderTypeMap['singleOrder']
  }
  if (hasOrderNo && hasNoOrderNo) {
    arr.forEach((item) => {
      if (!item.order_no) {
        item.partHasOrder = true
      }
    })
  }
  if (hasRoom && hasNoRoom) {
    arr.forEach((item) => {
      if (!item.room_name) {
        item.partHasRoom = true
      }
    })
  }
  const orderCounts = arr.reduce((accumulator, current) => {
    const key = current.order_no
    if (!accumulator[key]) {
      accumulator[key] = []
    }
    accumulator[key].push(current)
    return accumulator
  }, {})
  return {
    orderType,
    orderCounts,
    hasOrderNo,
    hasNoOrderNo,
    hasRoom,
    hasNoRoom,
    OrderTypeMap,
  }
}
// 处理web和客户端通信时的数据
export function dataToQt(qtData: any, oriArr?: any) {
  const paibanDataObj = {
    layoutData: store.state.preLayoutData,
    paibanData: oriArr ?? store.state.paibanData,
    process_setting_id: store.state.ncSetting.process_setting_id,
    process_setting_name: store.state.ncSetting.process_setting_name,
    recodeSelectPart: store.state.recodeSelectPart,
    recodeSelectSurplus: store.state.recodeSelectSurplus,
    thinkerxMaterialKeys: store.state.thinkerxMaterialKeys,
  }
  const ncData = {
    layoutData: paibanDataObj,
    ncData: qtData,
    process_setting_id: store.state.ncSetting.process_setting_id,
  }
  const win = window as any

  win.webToQt.ncDataToQtV2
    ? win.webToQt.ncDataToQtV2(ncData)
    : win.webToQt.ncDataToQt(qtData)
}

/**
 * 检查是否需要合并子文件夹
 * @param key 是否在合并文件夹选项中
 * @returns true false
 */
export function checkIsMergeFolder(key: string) {
  const { merge_folder_list, platePropertyMergeFile } =
    store.state.mergeFolderSetting ?? store.state.ncSetting.customizeFolder
  return (
    platePropertyMergeFile &&
    merge_folder_list.length > 1 &&
    merge_folder_list.includes(key)
  )
}

/**
 * 根据原始路径来获得子文件根据大板长宽控制文件夹的名称生成以及匹配
 * @param {*} bigPlank
 * @param {*} ncSetting
 * @param {string} [connector] 连接符
 * @returns
 */
export function getChildrenFolderFileSize(
  bigPlank: IBigPlank,
  ncSetting: INcSetting,
  connector = '*'
) {
  // 此函数返回的是没有 xy 互换样子的尺寸
  const { xyReverse } = ncSetting
  // 恢复回没有xy互换的样子获取板件尺寸，可以不关心旋转
  const path = xyReverse ? xyPointReverse(bigPlank.path ?? []) : bigPlank.path
  const [minX = 0, minY = 0, maxX = 0, maxY = 0] = getPathMinMaxPoint(
    path ?? []
  )
  if (isLShapeSurplusBigPlank(bigPlank)) {
    // 有地方将原始值给改变了，所以需要调换回来
    const { width, height } = bigPlank.surplusInfo ?? {}
    const nSurplus = {
      ...bigPlank.surplusInfo,
      width: xyReverse ? height : width,
      height: xyReverse ? width : height,
    }
    // 余料后端始终都会使用这个字段,所以此处安装原始的数据生成路径并获取其长款，和后端对应上
    const path = genBigPlankPath({ surplusInfo: nSurplus } as any, ncSetting)
    const {
      longestHorizontalEdge,
      secondLongestHorizontalEdge,
      longestVerticalEdge,
      secondLongestVerticalEdge,
    } = getEdgeLengthsFromLShape(path ?? [])
    return `${toDecimal(longestVerticalEdge.size, 2)}(${toDecimal(
      secondLongestVerticalEdge.size,
      2
    )})${connector}${toDecimal(longestHorizontalEdge.size, 2)}(${toDecimal(
      secondLongestHorizontalEdge.size,
      2
    )})`
  }
  const width = maxX - minX
  const height = maxY - minY
  return `${toDecimal(height, 2)}${connector}${toDecimal(width, 2)}`
}

/**
 * @description 根据文件材质颜色厚度尺寸获取到一个名称
 * @param stock_key 材质:颜色:厚度:大板号
 * @param size 大板尺寸
 * @returns 文件名称
 */
export function getFolderClassifyNameByMatCodeTextureThickSize(
  stock_key: string,
  stock_num?: string | number
) {
  const target = collector.getOriginNcData()?.find((item: IBigPlank) => {
    // 两种匹配模式，对应着前端自己存储的数据和后端数据(前端数据只用stock_key匹配，因为 nc 数据在发生给后端时 stock_num 被重置过，已经找不到源数据了)
    if (!stock_num && stock_num !== 0) {
      return item.stockKey === stock_key
    }
    const str = `${item.texture}:${item.matCode}:${item.thick}`
    stock_num = typeof stock_num === 'string' ? stock_num : String(stock_num)
    const stockNumArr = stock_num.split(',')
    return (
      str === stock_key && stockNumArr.some((v) => v == String(item.stockNum))
    )
  })
  if (!target) return
  const size = getChildrenFolderFileSize(target, collector.getNcSetting())
  if (!size) return
  // 表现方式为宽 x 高
  const plankSize = (size ?? '').split('*').join('x')
  stock_key =
    stock_num || stock_num === 0
      ? stock_key
      : stock_key.split(':').slice(0, -1).join(':')
  const stockKey = stock_key.replace(/:/g, '_')
  return `${stockKey}_${plankSize}`
}

/**
 * @description 导出 nc 文件，基于自定义文件夹功能
 * @param ncSetting
 * @param ncFiles nc 接口数据，不能包含其他数据
 * @param tagFiles 标签数据，按照以前的逻辑来处理，内部结构类似 nc 接口数据
 * @param labelImageObj 排版图数据
 * @param customPaibanPdfObj 自定义排版图pdf数据
 * @param otherFiles 其他要添加的文件，目前只支持添加到根目录下,用于自定义文件夹构建器构建数据
 * @returns
 */
export async function genNcZipByCustomFolder(
  ncSetting: INcSetting,
  customFolderConfig: Folder,
  ncFiles: any[],
  tagFiles: any[],
  labelImageObj: any,
  customPaibanPdfObj: any,
  otherFiles?: {
    jsonBlob?: any
    productionBatchExcelData?: any[]
    workerShopBlob?: Blob | null
  },
  options?: {
    isNeedTrialProduct?: boolean
    pageType?: 'materialList' | 'paiban'
  }
) {
  const { isNeedTrialProduct = false, pageType = 'paiban' } = options ?? {}
  let { jsonBlob, productionBatchExcelData, workerShopBlob } = otherFiles ?? {}
  // 自定义文件夹导出时，json 同样写入用户 IP
  jsonBlob = await appendIpToJsonBlob(jsonBlob)
  // 排版页因为一些特殊的文件需要创建添加  后续逻辑复杂了 请将此处逻辑进行分离
  if (pageType === 'paiban') {
    customFolderConfig = addNewFolderByConfig(
      customFolderConfig,
      isNeedTrialProduct
    )
  }
  addNcFileToCollector(ncFiles)
  addNcTagToCollector(tagFiles, ncSetting)
  await addPaibanImageToCollector(labelImageObj, customPaibanPdfObj)
  addNcOtherFilesToCollector(
    isNeedTrialProduct,
    jsonBlob,
    productionBatchExcelData,
    workerShopBlob
  )
  const folderStructure = new FolderStructureBuilder(
    collector,
    customFolderConfig
  )
  folderStructure.run()
  const exporter = new Exporter(folderStructure)
  return {
    folderStructure,
    exporter,
  }
}
/**
 * @description 添加其他nc 文件夹里面相关的文件到自定义文件夹收集器中 如(试生产图片/可导入 json)
 * @returns
 */
export async function addNcOtherFilesToCollector(
  isNeedTrialProduct: boolean,
  jsonBlob: any,
  productionBatchExcelData: any,
  workerShopBlob?: Blob | null
) {
  // 收集生产文件
  if (isNeedTrialProduct) {
    const sampleImage = [
      sampleCabinet1,
      sampleCabinet2,
      sampleCabinet3,
      sampleCabinet4,
    ]
    sampleImage.forEach((v, idx) => {
      collector.addFiles({
        type: 'trial_product_image',
        ext: '.png',
        name: `试生产样品柜样例图${idx + 1}`,
        raw: { kind: 'url', value: new URL(v, window.location.href).href },
      })
    })
  }
  // 收集排版json文件
  if (jsonBlob) {
    collector.addFiles({
      type: 'layout_json',
      ext: '.json',
      name: '排版数据',
      raw: { kind: 'arrayBuffer', value: jsonBlob },
      specialHandler: (fileNode: FileNode, parentNode: FolderNode) => {
        fileNode.name = parentNode.name ?? '排版数据'
      },
    })
  }
  if (productionBatchExcelData && !window.webToQt) {
    collector.addFiles({
      type: 'product_export_batch_excel',
      ext: '.xlsx',
      name: '生产进度跟踪批次导入表格',
      raw: { kind: 'blob', value: productionBatchExcelData },
      specialHandler: (fileNode: FileNode, parentNode: FolderNode) => {
        fileNode.name = parentNode.name ?? '排版数据'
      },
    })
  }
  if (workerShopBlob && !window.webToQt) {
    collector.addFiles({
      type: 'worker_shop_excel',
      ext: '.xlsx',
      name: '采购单',
      raw: { kind: 'blob', value: workerShopBlob },
      specialHandler: (fileNode: FileNode, parentNode: FolderNode) => {
        fileNode.name = parentNode.name ?? '采购单'
      },
    })
  }
}
/** @description 添加排版图到收集器 */
export async function addPaibanImageToCollector(
  labelImageObj: Record<
    number | string,
    {
      url: string
      name: string
      index: number
      stock_key: string
      size: string
      plankUniqueId: string
      ext: string
    }[]
  >,
  customPaibanPdfObj: any
) {
  // 添加排版图图片到收集器
  Object.values(labelImageObj).forEach((arr) => {
    arr.forEach((v) => {
      collector.addFiles({
        // 自定义文件夹用于寻找板件的 id （id可能是大板id，也可能是小板id）
        findId: v.plankUniqueId,
        name: removeFileNameExt(v.name, v.ext || '.jpg'),
        raw: { kind: 'base64', value: v.url },
        // 文件的类型
        type: 'paiban_image',
        ext: v.ext || '.jpg',
        datasource: 'bigPlank',
      })
    })
  })
  // 添加排版图数据到 PDF
  for (const v of Object.values(customPaibanPdfObj) as any) {
    if (!v.svg.length) continue
    collector.addFiles({
      name: v.name,
      raw: { kind: 'arrayBuffer', value: await convertPaibanPdfToBinary(v) },
      type: 'paiban_pdf',
      ext: '.pdf',
    })
  }
}
/** @description 添加接口返回的标准 nc 文件数据到收集器 */
export function addNcFileToCollector(ncFiles: NcFile[]) {
  ncFiles.forEach((v) => {
    // 文件名称在后端处理中可能已经携带后缀，如果存在则精准的删除文件名上的后缀，采用标明后缀的方式使用
    const name =
      removeFileNameExt(v.file_display_name, v.file_suffix) ||
      translateLang('未命名')
    collector.addFiles({
      // 自定义文件夹用于寻找板件的 id （id可能是大板id，也可能是小板id）
      findId: v.plankUniqueId,
      name,
      raw: { kind: 'url', value: `${OSS_NC_BASE_URL}/${v.filename}` },
      // 文件的类型
      type: v.file_tag,
      ext: v.file_suffix,
      folderGroupId: v.folder_group_id,
      datasource: 'bigPlank',
    })
  })
}
/** @description 添加标签文件（image/pdf）到收集器 */
export function addNcTagToCollector(ncFiles: any[], ncSetting: INcSetting) {
  const tagFiles = ncFiles.filter((v) => v.fileType === 'tag')
  /**
   * @description 标签（pdf/image）数据，返回统一格式以支持自定义文件夹构建器
   */
  const preprocessingTagData = (data: any, type: string, name: string) => {
    const result: any[] = []
    const dealPdf = () => {
      const obj: FileSource = {
        name,
        raw: { kind: 'arrayBuffer', value: data as ArrayBuffer },
        // 文件的类型
        type: 'label_pdf',
        ext: '.pdf',
      }
      result.push(obj)
    }
    const dealImage = () => {
      data.forEach((v: any) => {
        const obj: FileSource = {
          findId: v.plankId,
          name: v.fileName,
          raw: { kind: 'base64', value: v.url },
          // 文件的类型
          type: 'label_image',
          ext: `.${ncSetting?.labelImageFormat ?? 'jpg'}`,
          datasource: 'plank',
        }
        result.push(obj)
      })
    }
    type === 'pdf' ? dealPdf() : dealImage()
    return result
  }
  tagFiles.forEach((v) => {
    if (v.exportType === 'pdf') {
      collector.addFiles(
        preprocessingTagData(v.fileData, 'pdf', v.file_display_name)
      )
    } else if (v.exportType === 'zip') {
      collector.addFiles(
        preprocessingTagData(v.tagData, 'image', v.file_display_name)
      )
    }
  })
}

/**
 * @description 添加一些临时的文件夹
 * @param customFolderConfig
 * @param isNeedTrialProduct
 */
export function addNewFolderByConfig(
  customFolderConfig: Folder,
  isNeedTrialProduct: boolean
) {
  // 文件管理器实际会修改源数据 所以需要拷贝
  customFolderConfig = window.$copy(customFolderConfig) as Folder
  const folderManage = new FolderTreeManager(customFolderConfig)
  // 添加试生产文件夹
  if (isNeedTrialProduct) {
    const trialProductFolder = createFolder({
      nameLimitSize: 1000,
      name: '试生产样品柜样例图',
      pid: 'root',
      nameFields: [{ type: 'text', value: '试生产样品柜样例图' }],
      accept: {
        classifyChains: [],
        supportedFiles: [
          {
            type: 'trial_product_image',
            isFile: true,
            ext: '',
            id: '',
            name: '',
            pid: 'root',
          },
        ],
      },
    })
    folderManage.createFolder(trialProductFolder)
  }
  const node = folderManage.getTreeNode()
  // 因为产品不让我把源 json放到自定义文件夹的选项中，所以这里手动让根目录支持排版 json 文件，后面收集这个文件即可
  node.accept.supportedFiles.push({
    type: 'layout_json',
    isFile: true,
    ext: '.json',
    id: '',
    name: '',
    pid: 'root',
  })
  // 生成进度跟着批次文件
  node.accept.supportedFiles.push({
    type: 'product_export_batch_excel',
    isFile: true,
    ext: '.xlsx',
    id: '',
    name: '',
    pid: 'root',
  })
  // 采购单文件
  node.accept.supportedFiles.push({
    type: 'worker_shop_excel',
    isFile: true,
    ext: '.xlsx',
    id: '',
    name: '',
    pid: 'root',
  })
  return node
}

/**
 * @description 根据去除文件名上的后缀
 * @param name 文件名
 * @param ext 后缀
 * @returns 文件名
 */
function removeFileNameExt(name: string, ext: string) {
  return name.endsWith(ext) && ext ? name.slice(0, -ext.length) : name
}
